import GraphicObject from './GraphicObject';

const currentShaderProgram = Symbol('Current shader program');

export default class ShaderProgram extends GraphicObject {

	_attrib = null;
	_uniform = null;
	_uniformSetter = null;

	static getTypeInfo(gl, type, size) {
		switch (type) {
		case gl.BOOL:
			return { vector_size: 1, array_size: size, suffix: '1iv' };
		case gl.FLOAT:
			return { vector_size: 1, array_size: size, suffix: '1fv' };
		case gl.FLOAT_VEC2:
			return { vector_size: 2, array_size: size, suffix: '2fv' };
		case gl.FLOAT_VEC3:
			return { vector_size: 3, array_size: size, suffix: '3fv' };
		case gl.FLOAT_VEC4:
			return { vector_size: 4, array_size: size, suffix: '4fv' };
		case gl.INT:
			return { vector_size: 1, array_size: size, suffix: '1iv' };
		case gl.INT_VEC2:
			return { vector_size: 2, array_size: size, suffix: '2iv' };
		case gl.INT_VEC3:
			return { vector_size: 3, array_size: size, suffix: '3iv' };
		case gl.INT_VEC4:
			return { vector_size: 4, array_size: size, suffix: '4iv' };
		case gl.SAMPLER_2D:
			return { type: 'texture', vector_size: 1, array_size: null, suffix: '1i' };
		case gl.FLOAT_MAT2:
			return { matrix_size: 2, array_size: size, suffix: 'Matrix2fv' };
		case gl.FLOAT_MAT3:
			return { matrix_size: 3, array_size: size, suffix: 'Matrix3fv' };
		case gl.FLOAT_MAT4:
			return { matrix_size: 4, array_size: size, suffix: 'Matrix4fv' };
		default: throw new Error(`Unrecognised data type: 0x${type.toString(16)}`);
		}
	}

	constructor(gl, vertex, fragment) {
		const program = gl.createProgram();
		if (!program) {
			throw new Error('Failed to create shader program');
		}
		gl.attachShader(program, vertex);
		gl.attachShader(program, fragment);
		gl.linkProgram(program);
		if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
			console.log({ vertex, fragment });
			console.error(gl.getProgramInfoLog(program));
			throw new Error('Failed to link shader program');
		}
		super(gl, program);
		/* Attribute locations */
		this._attrib = new Map();
		const attrib_count = gl.getProgramParameter(program, gl.ACTIVE_ATTRIBUTES);
		for (let i = 0; i < attrib_count; i++) {
			const info = gl.getActiveAttrib(program, i);
			const name = info.name;
			const index = gl.getAttribLocation(program, name);
			this._attrib.set(name, index);
		}
		/* Uniform locations */
		this._uniform = new Map();
		this._uniformSetter = new Map();
		const uniform_count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
		for (let i = 0; i < uniform_count; i++) {
			const info = gl.getActiveUniform(program, i);
			const name = info.name;
			const index = gl.getUniformLocation(program, name);
			this._uniform.set(name, index);
			/* Bind setters */
			const { matrix_size, vector_size, suffix } = ShaderProgram.getTypeInfo(gl, info.type, info.size);
			const setterFn = gl[`uniform${suffix}`].bind(gl);
			if (!setterFn) {
				throw new Error(`Unable to find uniform${suffix}`);
			}
			const setter = value => {
				if (vector_size === 1 && typeof value === 'number') {
					value = [value];
				}
				if (matrix_size) {
					setterFn(index, false, value);
				} else {
					setterFn(index, value);
				}
			};
			this._uniformSetter.set(name, setter);
		}
	}

	select() {
		const gl = this._gl;
		if (gl[currentShaderProgram] !== this) {
			gl.useProgram(this._object);
			gl[currentShaderProgram] = this;
		}
	}

	getAttributeLocation(name) {
		const index = this._attrib.get(name);
		if (index === void 0) {
			throw new Error(`Attribute "${name}" does not exist in this shader program`);
		}
		return index;
	}

	getUniformLocation(name) {
		const index = this._uniform.get(name);
		if (index === void 0) {
			throw new Error(`Uniform "${name}" does not exist in this shader program`);
		}
		return index;
	}

	setUniform(name, value) {
		const setter = this._uniformSetter.get(name);
		if (!setter) {
			throw new Error(`Invalid uniform: ${name}`);
		}
		try {
			setter(value);
		} catch (e) {
			console.log(e);
			throw new Error(`Failed to set uniform "${name}"`);
		}
	}

	release() {
		this._gl.deleteProgram(this._object);
		super.release();
	}

}
